import { LinkedinLogo } from "@phosphor-icons/react";
import clsx from "clsx";
import { useState } from "react";

import { Button } from "common/components/ui/Button";
import {
  DialogBody,
  DialogClose,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  DrawerBody,
  DrawerClose,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import {
  typedObjectEntries,
  typeSafeObjectFromEntries,
} from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { LEAD_LIST_SOURCE_TYPES } from "../../constants";
import useLeadListSourceMutations from "../../datahooks/useLeadListSourceMutations";
import ConnectCrm from "./ConnectCrm";
import CustomList from "./CustomList";
import LinkedInImport from "./LinkedInImport";

interface AddSourceFlowProps {
  listId: string;
  onAddSuccess: () => void;
}

const SOURCE_CATEGORIES_KEYS = ["list", "drip", "crm"] as const;
const CATEGORIES = {
  linkedin: {
    icon: LinkedinLogo,
    label: "Import from LinkedIn",
    subtitle: "Search, Sales Navigator, Posts, Events, Groups",
    iconContainerClassName: "text-blue-500 border-blue-200 bg-blue-50",
  },
  ...typeSafeObjectFromEntries(
    SOURCE_CATEGORIES_KEYS.map((key) => {
      const { icon, label, subtitle, iconContainerClassName } =
        LEAD_LIST_SOURCE_TYPES[key];
      return [key, { icon, label, subtitle, iconContainerClassName }] as const;
    }),
  ),
} as const;

export default function AddSourceFlow({
  listId,
  onAddSuccess,
}: AddSourceFlowProps) {
  const isDesktop = useTwBreakpoint("lg");

  const [selectedCategory, setSelectedCategory] =
    useState<keyof typeof CATEGORIES>();
  const [isNextStep, setIsNextStep] = useState(false);

  const { createSource, isCreatingSource } = useLeadListSourceMutations();

  function onBack() {
    setIsNextStep(false);
    setSelectedCategory(undefined);
  }

  function onCreateSource(
    params: Parameters<typeof createSource>["0"]["params"],
  ) {
    createSource({ listId, params }).then(() => {
      onAddSuccess();
    });
  }

  const Body = isDesktop ? DialogBody : DrawerBody;

  let mainComponent = (
    <Body>
      <div className="flex flex-col lg:grid lg:grid-cols-2 gap-2">
        {typedObjectEntries(CATEGORIES).map(
          ([key, { label, subtitle, icon: Icon, iconContainerClassName }]) => {
            return (
              <button
                onClick={() => {
                  setSelectedCategory(key);
                }}
                type="button"
                className={clsx(
                  "p-5 flex items-center gap-x-5 gap-y-1 bg-black-50 border-black-50 rounded-5xl border",
                  key === selectedCategory
                    ? "border-purple-500"
                    : "border-transparent hover:bg-black-100 hover:border-black-100",
                  key === "drip" && "opacity-50 hover:!bg-black-50",
                )}
                key={key}
                disabled={key === "drip"}
              >
                <div
                  className={clsx(
                    "size-12 rounded-2xl flex items-center justify-center shrink-0 border",
                    iconContainerClassName,
                  )}
                >
                  <Icon className="size-5" />
                </div>
                <div className="flex flex-col gap-y-1">
                  <span className="text-xs-bold text-left">{label}</span>
                  <span className="text-xxs text-black-400 text-left">
                    {subtitle}
                  </span>
                </div>
              </button>
            );
          },
        )}
      </div>
    </Body>
  );

  if (isNextStep) {
    if (selectedCategory === "crm") {
      mainComponent = (
        <ConnectCrm
          createSource={onCreateSource}
          isCreatingSource={isCreatingSource}
          goBack={onBack}
        />
      );
    } else if (selectedCategory === "linkedin") {
      mainComponent = (
        <LinkedInImport
          createSource={onCreateSource}
          isCreatingSource={isCreatingSource}
          goBack={onBack}
        />
      );
    } else if (selectedCategory === "list") {
      mainComponent = (
        <CustomList
          createSource={onCreateSource}
          isCreatingSource={isCreatingSource}
          goBack={onBack}
        />
      );
    }
  }
  const Header = isDesktop ? DialogHeader : DrawerHeader;
  const Close = isDesktop ? DialogClose : DrawerClose;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;

  return (
    <>
      {/* Header */}
      {!isNextStep && <Header title="Add leads" />}
      {mainComponent}
      {!isNextStep && (
        <Footer className="justify-between">
          <Close asChild>
            <Button variant="secondary-black">Cancel</Button>
          </Close>
          <Button
            onClick={() => setIsNextStep(true)}
            disabled={!selectedCategory}
          >
            Next
          </Button>
        </Footer>
      )}
    </>
  );
}
